import { useMemo, useState, useEffect, type MouseEvent } from "react";

type FeedMedia = {
  local_path: string;
  cdn_url?: string;
  media_type: string;
};

type FeedPost = {
  id: string;
  caption: string;
  permalink: string;
  timestamp: string;
  media: FeedMedia[];
};

const generateSlug = (caption: string, id: string): string => {
  if (!caption) return id;

  const words = caption
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .split(/\s+/)
    .slice(0, 5)
    .join('-');

  return `${words}-${id}`;
};

const shouldHandleInApp = (event: MouseEvent<HTMLAnchorElement>): boolean => {
  return (
    !event.defaultPrevented &&
    event.button === 0 &&
    !event.metaKey &&
    !event.altKey &&
    !event.ctrlKey &&
    !event.shiftKey
  );
};

function CaptionText({ text }: { text: string }) {
  const parts = text.split(/([@#][\w.\u00C0-\u024F]+)/g);

  return (
    <p className="whitespace-pre-wrap leading-relaxed">
      {parts.map((part, index) => {
        if (part.startsWith("@")) {
          const handle = part.slice(1);
          return (
            <a
              key={`mention-${index}`}
              href={`https://www.instagram.com/${handle}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 dark:text-blue-400 hover:underline"
            >
              {part}
            </a>
          );
        }
        if (part.startsWith("#")) {
          return (
            <span key={`hashtag-${index}`} className="text-blue-500 dark:text-blue-400">
              {part}
            </span>
          );
        }
        return <span key={`text-${index}`}>{part}</span>;
      })}
    </p>
  );
}

function ImageCarousel({ media, postId, altText }: { media: FeedMedia[]; postId: string; altText: string }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
  };

  if (media.length === 0) {
    return (
      <div className="w-full aspect-square bg-muted flex items-center justify-center text-muted-foreground">
        No media
      </div>
    );
  }

  const currentMedia = media[currentIndex]!;
  const mediaSrc = currentMedia.cdn_url ?? `/${currentMedia.local_path}`;
  const isVideo = currentMedia.media_type === "video";

  return (
    <div className="relative w-full aspect-square bg-black group">
      {isVideo ? (
        <video
          src={mediaSrc}
          controls
          className="w-full h-full object-contain"
          preload="metadata"
          aria-label={altText}
        />
      ) : (
        <img
          src={mediaSrc}
          alt={altText}
          className="w-full h-full object-contain"
          loading="lazy"
          width="1080"
          height="1080"
        />
      )}

      {media.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
            aria-label="Previous image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={goToNext}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
            aria-label="Next image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
            {media.map((_, index) => (
              <button
                key={`${postId}-dot-${index}`}
                onClick={() => setCurrentIndex(index)}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  index === currentIndex ? "bg-white w-2" : "bg-white/60"
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>

          <div className="absolute top-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
            {currentIndex + 1} / {media.length}
          </div>
        </>
      )}
    </div>
  );
}

function formatDate(timestamp: string): string {
  const date = new Date(timestamp);
  const month = date.toLocaleString('en-US', { month: 'long' });
  const day = date.getDate();
  const year = date.getFullYear();
  
  // Add ordinal suffix (st, nd, rd, th)
  const suffix = (day: number) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };
  
  return `${month} ${day}${suffix(day)}, ${year}`;
}

export function Feed({ posts, onPostClick }: { posts: FeedPost[]; onPostClick?: (postId: string) => void }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Extract all unique hashtags from posts
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    posts.forEach(post => {
      const matches = post.caption.match(/#[^\s#]+/g);
      if (matches) {
        matches.forEach(tag => tagSet.add(tag.toLowerCase()));
      }
    });
    return Array.from(tagSet).sort((a, b) => 
      a.localeCompare(b, 'en', { sensitivity: 'base' })
    );
  }, [posts]);

  // Filter posts by search query and selected tag
  const filteredPosts = useMemo(() => {
    let filtered = posts;

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(post =>
        post.caption.toLowerCase().includes(query)
      );
    }

    // Filter by selected tag
    if (selectedTag) {
      filtered = filtered.filter(post =>
        post.caption.toLowerCase().includes(selectedTag)
      );
    }

    return filtered;
  }, [posts, searchQuery, selectedTag]);

  return (
    <>
      {/* Mobile Header with Search & Hamburger */}
      <div className="lg:hidden sticky top-16 z-40 bg-background border-b mb-6 -mx-4 px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Toggle filters menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
          
          <input
            type="text"
            placeholder="Search posts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-3 py-2 text-base border rounded-lg bg-background focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar Drawer */}
      <aside
        className={`lg:hidden fixed top-0 left-0 h-full w-80 bg-card border-r shadow-xl z-50 transform transition-transform duration-300 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] ${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="p-6 space-y-6">
          {/* Close Button */}
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="absolute top-4 right-4 p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Profile Picture */}
          <div className="flex justify-center pt-4">
            <img 
              src="https://images.gizemon.wine/instagram/3677990978309147903_1.jpg"
              alt="Profile"
              className="w-32 h-32 rounded-full object-cover"
              width="128"
              height="128"
            />
          </div>

          {/* Mini Bio */}
          <div className="text-center space-y-2 pb-6 border-b">
            <h2 className="text-xl font-bold">Gizem</h2>
            <p className="text-sm text-muted-foreground">
              I like wine. I study wine. Ok. I love wine.
            </p>
          </div>

          {/* Tag Filter */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Filter by tag
            </label>
            <div className="flex flex-wrap gap-1.5">
              {selectedTag && (
                <button
                  onClick={() => {
                    setSelectedTag(null);
                    setMobileMenuOpen(false);
                  }}
                  className="px-2.5 py-1 text-xs rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                >
                  Clear ×
                </button>
              )}
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => {
                    setSelectedTag(selectedTag === tag ? null : tag);
                    setMobileMenuOpen(false);
                  }}
                  className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                    selectedTag === tag
                      ? "bg-blue-500 text-white"
                      : "bg-muted hover:bg-muted/80 text-foreground"
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Results count */}
          <div className="text-xs text-muted-foreground pt-2 border-t">
            Showing {filteredPosts.length} of {posts.length} posts
          </div>
        </div>
      </aside>

      {/* Desktop Sidebar */}
      <div className="flex gap-8 items-start">
        <aside className="hidden lg:block w-80 flex-shrink-0 sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        <div className="bg-card rounded-lg border shadow-lg p-6 space-y-6">
          {/* Profile Picture */}
          <div className="flex justify-center">
            <img 
              src="https://images.gizemon.wine/instagram/3677990978309147903_1.jpg"
              alt="Profile"
              className="w-32 h-32 rounded-full object-cover"
              width="128"
              height="128"
            />
          </div>

          {/* Mini Bio */}
          <div className="text-center space-y-2 pb-6 border-b">
            <h2 className="text-xl font-bold">Gizem</h2>
            <p className="text-sm text-muted-foreground">
              I like wine. I study wine. Ok. I love wine.
            </p>
          </div>

          {/* Filters Section */}
          <div className="space-y-4">
            {/* Search Input */}
            <div>
              <label htmlFor="search" className="block text-sm font-medium mb-2">
                Search posts
              </label>
              <input
                id="search"
                type="text"
                placeholder="Search my posts"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 text-base border rounded-lg bg-background focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Tag Filter */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Filter by tag
              </label>
              <div className="flex flex-wrap gap-1.5">
                {selectedTag && (
                  <button
                    onClick={() => setSelectedTag(null)}
                    className="px-2.5 py-1 text-xs rounded-full bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                  >
                    Clear ×
                  </button>
                )}
                {allTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                    className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                      selectedTag === tag
                        ? "bg-blue-500 text-white"
                        : "bg-muted hover:bg-muted/80 text-foreground"
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Results count */}
            <div className="text-xs text-muted-foreground pt-2 border-t">
              Showing {filteredPosts.length} of {posts.length} posts
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 space-y-8">

      {/* Posts Grid */}
      {filteredPosts.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          No posts found matching your filters.
        </div>
      ) : (
        <div className="max-w-2xl mx-auto space-y-8">
          {filteredPosts.map(post => {
            // Generate alt text from caption (same as og:title)
            const cleanCaption = post.caption ? post.caption.replace(/\s+/g, ' ').trim() : "";
            const altText = cleanCaption 
              ? `${cleanCaption.slice(0, 60)}${cleanCaption.length > 60 ? "..." : ""}`
              : "Wine tasting note";
            const postPath = `/posts/${generateSlug(post.caption, post.id)}`;

            return (
            <article key={post.id} className="rounded-lg border bg-card text-card-foreground shadow-lg overflow-hidden">
              <ImageCarousel media={post.media} postId={post.id} altText={altText} />
              
              <div className="p-6 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground mb-3">
                      <time dateTime={post.timestamp}>{formatDate(post.timestamp)}</time>
                    </p>
                    {post.caption ? (
                      <CaptionText text={post.caption} />
                    ) : (
                      <p className="text-muted-foreground">No caption</p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <a
                    href={post.permalink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center text-sm font-medium text-blue-500 hover:text-blue-600 transition-colors"
                  >
                    View on Instagram
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  {onPostClick && posts.length > 1 && (
                    <a
                      href={postPath}
                      onClick={(event) => {
                        if (!shouldHandleInApp(event)) {
                          return;
                        }
                        event.preventDefault();
                        onPostClick(post.id);
                      }}
                      className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      View Post
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </a>
                  )}
                </div>
              </div>
            </article>
            );
          })}
        </div>
      )}
      </div>
      </div>
    </>
  );
}
